import { ReactNode } from 'react';
import { Button, Empty, lucideIcons } from '@round/creatorbase-ui';
import { OutreachRequestsEmptyStateVariant } from './helpers';
import styles from './OutreachRequestsEmptyState.module.css';

const { CircleAlert, Inbox, Plus, SearchX, Users } = lucideIcons;

const GENERIC_ERROR_MESSAGE = 'Outreach requests could not be loaded.';

type Props = {
    variant: OutreachRequestsEmptyStateVariant;
    errorMessage?: string | null;
    onCreateRequest: () => void;
};

export const OutreachRequestsEmptyState = ({ variant, errorMessage, onCreateRequest }: Props) => (
    <div className={styles.container}>
        <Empty className={styles.empty}>{getVariantContent({ variant, errorMessage, onCreateRequest })}</Empty>
    </div>
);

function getVariantContent({ variant, errorMessage, onCreateRequest }: Props): ReactNode {
    switch (variant) {
        case 'noClient':
            return (
                <>
                    <Empty.Media variant="icon">
                        <Users />
                    </Empty.Media>
                    <Empty.Title>No client selected</Empty.Title>
                    <Empty.Description>
                        Outreach requests belong to a client. Select one to see its requests.
                    </Empty.Description>
                </>
            );
        case 'noRequests':
            return (
                <>
                    <Empty.Media variant="icon">
                        <Inbox />
                    </Empty.Media>
                    <Empty.Title>No outreach requests yet</Empty.Title>
                    <Empty.Description>
                        Create a request to start collecting creators for this client.
                    </Empty.Description>
                    <Empty.Content>
                        <Button size="xs" onClick={onCreateRequest}>
                            <Plus /> Create request
                        </Button>
                    </Empty.Content>
                </>
            );
        case 'noMatches':
            return (
                <>
                    <Empty.Media variant="icon">
                        <SearchX />
                    </Empty.Media>
                    <Empty.Title>No matching requests</Empty.Title>
                    <Empty.Description>
                        No requests match the current filters. Clear or widen them to see more.
                    </Empty.Description>
                </>
            );
        // Reloading the page is what recovers this, so there is deliberately nothing to press here.
        case 'error':
            return (
                <>
                    <Empty.Media variant="icon">
                        <CircleAlert />
                    </Empty.Media>
                    <Empty.Title>Something went wrong</Empty.Title>
                    <Empty.Description>{errorMessage || GENERIC_ERROR_MESSAGE}</Empty.Description>
                </>
            );
    }
}
